import React from "react";
import {
  ChartDataset,
  ChartStyle,
  ChartType
} from "state/ui/editor/elements/ChartElement";
import ColumnChart from "./chartRenderers/ColumnChart";
import BarChart from "./chartRenderers/BarChart";
import LineChart from "./chartRenderers/LineChart";
import AreaChart from "./chartRenderers/AreaChart";
import PieChart from "./chartRenderers/PieChart";
import DonutChart from "./chartRenderers/DonutChart";
import { legendGutterFor, seriesColorAt } from "./chartScales";

export interface ChartElementData {
  uniqueId: string;
  width: number;
  height: number;
  opacity: number;
  chartType: ChartType;
  dataset: ChartDataset;
  style: ChartStyle;
}

interface ChartRendererProps {
  dataset: ChartDataset;
  style: ChartStyle;
  width: number;
  height: number;
}

const RENDERERS: Record<
  ChartType,
  (props: ChartRendererProps) => React.ReactElement
> = {
  column: ColumnChart,
  bar: BarChart,
  line: LineChart,
  area: AreaChart,
  pie: PieChart,
  donut: DonutChart
};

const BASELINE_WIDTH = 480;
const BASELINE_HEIGHT = 360;
const MIN_FONT_SIZE = 1;

const Legend = ({
  dataset,
  style,
  width,
  height,
  chartType
}: ChartRendererProps & { chartType: ChartType }) => {
  if (!style.showLegend) return null;
  // Pie/donut render slices from the first row's values, one slice per header,
  // so their legend labels headers. Cartesian renderers color one series per
  // row (seriesColors[rowIndex]); the legend must match, so it always labels
  // rows — even for a single series, where a header-based legend would show
  // several colors the monochrome single-series bars never use.
  const usesHeaderLabels = chartType === "pie" || chartType === "donut";
  const labels = usesHeaderLabels
    ? dataset.headers
    : dataset.rows.map(row => row.label);
  const items = labels.map((label, index) => ({
    label,
    color: seriesColorAt(style.seriesColors, index)
  }));
  if (items.length === 0) return null;

  const swatchSize = Math.max(8, style.fontSize * 0.85);
  const legendGutter = legendGutterFor(style.fontSize);
  const legendTop = height - (legendGutter + swatchSize) / 2;
  const padding = 12;
  const availableWidth = Math.max(width - padding * 2, 1);

  // Estimate text width per label so each legend item gets space proportional
  // to its name length. 0.55 of font-size is a reasonable avg-char-width for
  // Roboto / common sans-serifs. Approximation is fine here — over/undershoot
  // by a few px doesn't matter for legend layout, and this works in static
  // SVG export contexts where getComputedTextLength isn't available.
  const swatchTextGap = 6;
  const avgCharWidth = style.fontSize * 0.55;
  const itemWidths = items.map(
    item => swatchSize + swatchTextGap + item.label.length * avgCharWidth
  );
  const totalContent = itemWidths.reduce((sum, w) => sum + w, 0);
  const minGap = Math.max(8, style.fontSize * 0.6);
  const idealGap = Math.max(minGap, style.fontSize * 1.6);
  const gapCount = Math.max(items.length - 1, 1);
  const gapByFit = (availableWidth - totalContent) / gapCount;
  const gap = Math.max(minGap, Math.min(idealGap, gapByFit));

  // Clip items whose box would extend past the chart's right edge. Without
  // this the legend bleeds into surrounding canvas (the wrapping <svg> uses
  // overflow: visible). A future multi-row layout can replace this — for
  // now, dropping out-of-bounds items is preferable to overlapping the
  // adjacent design. Always place at least the first item even if it
  // overflows, otherwise narrow charts with long labels would render a
  // blank legend strip with no series indicator at all.
  const rightLimit = width - padding;
  let cursor = padding;
  const placed: Array<{ x: number; item: (typeof items)[number] }> = [];
  for (let i = 0; i < items.length; i++) {
    const w = itemWidths[i] ?? 0;
    if (placed.length > 0 && cursor + w > rightLimit) break;
    placed.push({ x: cursor, item: items[i]! });
    cursor += w + gap;
  }

  return (
    <g>
      {placed.map(({ x, item }, index) => (
        <g key={`legend-${index}`} transform={`translate(${x}, ${legendTop})`}>
          <rect
            width={swatchSize}
            height={swatchSize}
            fill={item.color}
            rx={2}
          />
          <text
            x={swatchSize + swatchTextGap}
            y={swatchSize - 1}
            fontSize={style.fontSize}
            fill={style.color}
            fontFamily={style.fontFamily}
          >
            {item.label}
          </text>
        </g>
      ))}
    </g>
  );
};

interface Props {
  elementData: ChartElementData;
  isSelected?: boolean;
  onSelectItem?: (args: { append: boolean }) => void;
  style?: React.CSSProperties;
  connectDragSource?: (el: React.ReactElement) => React.ReactElement;
}

const Chart = ({
  elementData,
  onSelectItem,
  style: wrapperStyle,
  connectDragSource
}: Props) => {
  const { width, height, opacity, chartType, dataset, style } = elementData;

  const chartScale = Math.min(width / BASELINE_WIDTH, height / BASELINE_HEIGHT);
  const scaledStyle = {
    ...style,
    fontSize: Math.max(MIN_FONT_SIZE, style.fontSize * chartScale)
  };

  const Renderer = RENDERERS[chartType] || ColumnChart;

  const handleSelect = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (onSelectItem) {
      const append =
        "shiftKey" in e &&
        ((e as React.MouseEvent).shiftKey || (e as React.MouseEvent).metaKey);
      onSelectItem({ append: Boolean(append) });
    }
  };

  const chartNode = (
    <div
      style={{
        ...wrapperStyle,
        width,
        height,
        opacity,
        overflow: "visible",
        position: "relative"
      }}
      onClick={handleSelect}
      data-testid="chart-element"
    >
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        style={{ overflow: "visible" }}
      >
        <rect width={width} height={height} fill="transparent" />
        <Renderer
          dataset={dataset}
          style={scaledStyle}
          width={width}
          height={height}
        />
        <Legend
          dataset={dataset}
          style={scaledStyle}
          width={width}
          height={height}
          chartType={chartType}
        />
      </svg>
    </div>
  );

  return connectDragSource ? connectDragSource(chartNode) : chartNode;
};

export default Chart;
